import type { PrototypeUrlState, StateOf, StateOptions } from './prototype-url-state';

/**
 * Applies a state to the design's markup without design-specific code:
 * - every key lands on the root as `data-state-<key>` (camelCase keys become kebab-case), for the design's SCSS;
 * - an element with `data-prototype-when="status:empty status:error"` shows only while one of those pairs holds.
 */
export class PrototypeRender {
    public static apply<O extends StateOptions>(root: HTMLElement, urlState: PrototypeUrlState<O>, state: StateOf<O>) {
        for (const key of urlState.keys()) {
            root.setAttribute(`data-state-${PrototypeRender.kebab(key)}`, state[key]);
        }

        root.querySelectorAll<HTMLElement>('[data-prototype-when]').forEach(element => {
            const pairs = (element.getAttribute('data-prototype-when') ?? '').split(/\s+/).filter(pair => pair !== '');
            element.hidden = !pairs.some(pair => {
                const [key = '', value = ''] = pair.split(':');
                return urlState.isKey(key) && state[key] === value;
            });
        });
    }

    /** Pairs in `data-prototype-when` that name no state key or value: a typo there hides the element for good. */
    public static unknownConditions<O extends StateOptions>(root: ParentNode, urlState: PrototypeUrlState<O>): ReadonlyArray<string> {
        const unknown: Array<string> = [];
        root.querySelectorAll('[data-prototype-when]').forEach(element => {
            for (const pair of (element.getAttribute('data-prototype-when') ?? '').split(/\s+/)) {
                const [key = '', value = ''] = pair.split(':');
                if (pair !== '' && !(urlState.isKey(key) && urlState.allows(key, value))) {
                    unknown.push(pair);
                }
            }
        });

        return unknown;
    }

    private static kebab(key: string): string {
        return key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
    }
}
